import {cn} from '@sollapay/ui';
import {Badge, Icon, IconBox, Typography} from '@sollapay/ui/components';
import {AUTHORITY_ICON} from '@sollapay/web-core';

import {getPartyTypeIcon} from '@/utils';

import {AUTHORITY_PREFIX} from './creditor-account.utils';

import type {AuthorityDTO, BeneficiaryDTO} from '@sollapay/types';
import type {FieldComboboxOptionGroup, IconName} from '@sollapay/ui/components';
import type {TFunction} from 'i18next';
import type {ReactNode} from 'react';

interface PayeeOptionRowProps {
  icon: IconName;
  tone: 'tertiary' | 'brand';
  name: string;
  badge: string;
  /** Badge pushed to the row's far end — used for dropdown options. Selected-value display keeps it next to the name. */
  spread?: boolean;
}

function PayeeOptionRow({icon, tone, name, badge, spread}: PayeeOptionRowProps) {
  return (
    <div className={cn('flex items-center gap-2', spread && 'w-full justify-between')}>
      <div className="flex items-center gap-2">
        <IconBox icon={icon} size="xs" variant={tone} iconColor={tone} />
        <Typography size="sm" weight="semibold">
          {name}
        </Typography>
      </div>
      <Badge variant="neutral" size="sm">
        {badge}
      </Badge>
    </div>
  );
}

interface BuildPayeeOptionsParams {
  beneficiaries: BeneficiaryDTO[];
  authorities: AuthorityDTO[];
  t: TFunction<'trustAccounts'>;
  tAuthorities: TFunction<'authorities'>;
}

export function buildPayeeOptions({
  beneficiaries,
  authorities,
  t,
  tAuthorities
}: BuildPayeeOptionsParams): FieldComboboxOptionGroup[] {
  return [
    {
      key: 'beneficiaries',
      label: t('view.payouts.create.details.beneficiaryGroup'),
      icon: <Icon name="user" width={12} height={12} className="text-fg-tertiary" />,
      options: beneficiaries.map(beneficiary => ({
        value: beneficiary.id,
        searchText: beneficiary.fullName,
        label: (
          <PayeeOptionRow
            icon={getPartyTypeIcon(beneficiary.entityType)}
            tone="tertiary"
            name={beneficiary.fullName}
            badge={t(`partyRole.${beneficiary.role}`)}
            spread
          />
        )
      }))
    },
    {
      key: 'authorities',
      label: t('view.payouts.create.details.authoritiesGroup'),
      icon: <Icon name={AUTHORITY_ICON} width={12} height={12} className="text-fg-tertiary" />,
      options: authorities.map(authority => {
        const label = tAuthorities(`${authority.code}.label`);
        return {
          value: `${AUTHORITY_PREFIX}${authority.code}`,
          searchText: label,
          label: (
            <PayeeOptionRow
              icon={AUTHORITY_ICON}
              tone="brand"
              name={label}
              badge={tAuthorities(`${authority.code}.badge`)}
              spread
            />
          )
        };
      })
    }
  ];
}

/** Renders the same icon+name+badge row shown in the dropdown for the selected value, via FieldCombobox's `renderValue`. */
export function buildPayeeValueDisplay({
  beneficiaries,
  authorities,
  t,
  tAuthorities
}: BuildPayeeOptionsParams): (value: string) => ReactNode {
  return function renderPayeeValue(value) {
    const authorityCode = value.startsWith(AUTHORITY_PREFIX)
      ? value.slice(AUTHORITY_PREFIX.length)
      : null;
    if (authorityCode) {
      const authority = authorities.find(a => a.code === authorityCode);
      if (!authority) return null;
      const label = tAuthorities(`${authority.code}.label`);
      return (
        <PayeeOptionRow
          icon={AUTHORITY_ICON}
          tone="brand"
          name={label}
          badge={tAuthorities(`${authority.code}.badge`)}
        />
      );
    }

    const beneficiary = beneficiaries.find(b => b.id === value);
    if (!beneficiary) return null;
    return (
      <PayeeOptionRow
        icon={getPartyTypeIcon(beneficiary.entityType)}
        tone="tertiary"
        name={beneficiary.fullName}
        badge={t(`partyRole.${beneficiary.role}`)}
      />
    );
  };
}
